import React, { useState } from "react";
import { Search, Thermometer, Users } from "lucide-react";
import { motion } from "framer-motion";
import ResultsPopup from "./ResultsPopup";
import { hotSpringsData } from "../data/hotSprings";

const SearchSection = () => {
  const [searchData, setSearchData] = useState({
    temperature: "",
    guests: "",
    amenities: "",
  });
  const [showResults, setShowResults] = useState(false);
  const [filteredSprings, setFilteredSprings] = useState([]);

  const handleInputChange = (field, value) => {
    setSearchData((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const filterByTemperature = (springs, selectedRange) => {
    if (!selectedRange) return springs;
    
    let minSelected, maxSelected;
    if (selectedRange.endsWith('+')) {
      minSelected = parseInt(selectedRange);
      maxSelected = Infinity;
    } else {
      [minSelected, maxSelected] = selectedRange.split('-').map(Number);
    }

    return springs.filter(spring => {
      const tempRange = spring.temperature.match(/\d+/g);
      const springMin = parseInt(tempRange[0]);
      const springMax = parseInt(tempRange[1] || tempRange[0]);
      
      // Check if ranges overlap at all
      return (springMin <= maxSelected && springMax >= minSelected);
    });
  };

  const handleSearch = () => {
    const filtered = filterByTemperature(hotSpringsData, searchData.temperature);
    setFilteredSprings(filtered);
    setShowResults(true);
  };

  return (
    <section id="search" className="py-20 bg-white/50 backdrop-blur-sm">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          className="text-center mb-12"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          viewport={{ once: true }}
        >
          <h2 className="text-3xl sm:text-4xl font-serif font-bold text-gray-800 mb-4">
            Find Your Hot Spring
          </h2>
          <p className="text-lg text-gray-600 max-w-2xl mx-auto">
            Search through Mankato&#39;s premier hot springs and wellness
            destinations
          </p>
        </motion.div>

        <motion.div
          className="max-w-6xl mx-auto bg-white/80 backdrop-blur-md rounded-2xl shadow-xl border border-white/20 p-6 lg:p-8"
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.2 }}
          viewport={{ once: true }}
        >
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 lg:gap-6 mb-6">

            {/* Temperature Range */}
            <div className="space-y-2">
              <label className="block text-sm font-medium text-gray-700">
                Temperature
              </label>
              <div className="relative">
                <Thermometer className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                <select
                  value={searchData.temperature}
                  onChange={(e) =>
                    handleInputChange("temperature", e.target.value)
                  }
                  className="w-full pl-10 pr-4 py-3 border border-gray-200 rounded-lg focus:ring-2 focus:ring-spa-blue-500 focus:border-transparent transition-all duration-200 bg-white/70 backdrop-blur-sm appearance-none"
                >
                  <option value="">Any Temperature</option>
                  <option value="94-96">94-96°F</option>
                  <option value="97-99">97-99°F</option>
                  <option value="90-99">90-99°F</option>
                  <option value="100-102">100-102°F</option>
                  <option value="103-105">103-105°F</option>
                  <option value="100-104">100-104°F</option>
                  <option value="106-108">106-108°F</option>
                  <option value="105+">105°F+</option>
                </select>
              </div>
            </div>

            {/* Number of Guests */}
            <div className="space-y-2">
              <label className="block text-sm font-medium text-gray-700">
                Guests
              </label>
              <div className="relative">
                <Users className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                <select
                  value={searchData.guests}
                  onChange={(e) => handleInputChange("guests", e.target.value)}
                  className="w-full pl-10 pr-4 py-3 border border-gray-200 rounded-lg focus:ring-2 focus:ring-spa-blue-500 focus:border-transparent transition-all duration-200 bg-white/70 backdrop-blur-sm appearance-none"
                >
                  <option value="">Any Size</option>
                  <option value="1-2">1-2 Guests</option>
                  <option value="3-5">3-5 Guests</option>
                  <option value="6+">6+ Guests</option>
                </select>
              </div>
            </div>

            {/* Amenities */}
            <div className="space-y-2">
              <label className="block text-sm font-medium text-gray-700">
                Amenities
              </label>
              <select
                value={searchData.amenities}
                onChange={(e) => handleInputChange("amenities", e.target.value)}
                className="w-full px-4 py-3 border border-gray-200 rounded-lg focus:ring-2 focus:ring-spa-blue-500 focus:border-transparent transition-all duration-200 bg-white/70 backdrop-blur-sm appearance-none"
              >
                <option value="">All Amenities</option>
                <option value="spa">Full Spa Services</option>
                <option value="restaurant">Restaurant</option>
                <option value="accommodation">Accommodation</option>
                <option value="trails">Nature Trails</option>
              </select>
            </div>
          </div>

          {/* Search Button */}
          <div className="flex justify-center">
            <motion.button
              onClick={handleSearch}
              className="button-primary flex items-center space-x-2 text-lg px-8 py-3"
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
            >
              <Search className="w-5 h-5" />
              <span>Search Hot Springs</span>
            </motion.button>
          </div>
        </motion.div>

        {/* Popular Searches */}
        <motion.div
          className="text-center mt-8"
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          transition={{ duration: 0.6, delay: 0.4 }}
          viewport={{ once: true }}
        >
          <p className="text-sm text-gray-600 mb-3">Popular searches:</p>
          <div className="flex flex-wrap justify-center gap-2">
            {[
              "Natural Springs",
              "Spa Resorts",
              "Family-Friendly",
              "Romantic Getaway",
              "Wellness Retreats",
            ].map((tag, index) => (
              <button
                key={index}
                className="px-4 py-2 text-sm bg-spa-blue-50 text-spa-blue-700 rounded-full hover:bg-spa-blue-100 transition-colors duration-200"
              >
                {tag}
              </button>
            ))}
          </div>
        </motion.div>
      </div>

      {showResults && (
        <ResultsPopup 
          springs={filteredSprings} 
          onClose={() => setShowResults(false)} 
        />
      )}
    </section>
  );
};

export default SearchSection;
